import {Component, Fragment, type ErrorInfo, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {reportError} from '../../../common/errors/reportError'

interface EditorErrorBoundaryProps {
  children: ReactNode
  // Called when the boundary catches an error — lets the parent clear any
  // loading overlay it renders outside the boundary (otherwise the blur would
  // stay up on top of the error card).
  onError?: () => void
}

interface EditorErrorBoundaryState {
  error: Error | null
  retryKey: number
}

class EditorErrorBoundaryInner extends Component<
  EditorErrorBoundaryProps & {retryLabel: string; failedLabel: string; descriptionLabel: string},
  EditorErrorBoundaryState
> {
  state: EditorErrorBoundaryState = {error: null, retryKey: 0}

  static getDerivedStateFromError(error: Error): Partial<EditorErrorBoundaryState> {
    return {error}
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, 'EditorErrorBoundary', {componentStack: info.componentStack})
    this.props.onError?.()
  }

  handleRetry = () => {
    this.setState((prev) => ({error: null, retryKey: prev.retryKey + 1}))
  }

  render() {
    if (this.state.error) {
      return (
        <div style={styles.wrap}>
          <div style={styles.card}>
            <div style={styles.emoji} aria-hidden>
              🧊
            </div>
            <h3 style={styles.title}>{this.props.failedLabel}</h3>
            <p style={styles.description}>{this.props.descriptionLabel}</p>
            <button type="button" onClick={this.handleRetry} style={styles.btn}>
              {this.props.retryLabel}
            </button>
          </div>
        </div>
      )
    }
    // The Fragment-with-key preserves remount-on-retry without inserting a wrapping
    // <div> that would break the parent's flex/100%-height layout (R3F Canvas needs
    // height inheritance to size itself).
    return <Fragment key={this.state.retryKey}>{this.props.children}</Fragment>
  }
}

const EditorErrorBoundary = ({children, onError}: EditorErrorBoundaryProps) => {
  const {t} = useTranslation()
  return (
    <EditorErrorBoundaryInner
      onError={onError}
      retryLabel={t('sorry.reload')}
      failedLabel={t('editor.viewport.crashed')}
      descriptionLabel={t('editor.viewport.crashedDescription')}
    >
      {children}
    </EditorErrorBoundaryInner>
  )
}

export default EditorErrorBoundary

const styles = {
  wrap: {
    width: '100%',
    height: '100%',
    minHeight: 320,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(7, 6, 11, 0.6)',
    padding: 24,
  },
  card: {
    maxWidth: 380,
    textAlign: 'center',
    background: 'rgba(20, 17, 32, 0.7)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 12,
    padding: 24,
    color: '#F5F3FF',
    fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  emoji: {
    fontSize: 36,
    marginBottom: 8,
    lineHeight: 1,
  },
  title: {
    margin: '0 0 8px',
    fontSize: 16,
    fontWeight: 600,
  },
  description: {
    margin: '0 0 16px',
    fontSize: 13,
    color: '#B8B3CC',
    lineHeight: 1.5,
  },
  btn: {
    appearance: 'none' as const,
    cursor: 'pointer',
    background: '#7C5CFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
  },
} as const
